import type { CampaignDetail, CampaignPauseReason } from '@neuroedge/shared';
import { Button, Card, useToast } from '../../components/ui';
import { usePauseCampaign, useResumeCampaign } from '../../api/campaigns';
import { QualityBadge, limitLabel } from '../../components/whatsapp/NumberHealth';

const PAUSED: Record<CampaignPauseReason, string> = {
  MANUAL: 'Paused by your team.',
  SPAM_WARNING:
    'Paused automatically: Meta is limiting this number because customers flagged its ' +
    'messages. Check the message and who it goes to before resuming.',
  LOW_QUALITY:
    "Paused automatically: this number's quality rating is low. Sending more now could get " +
    'the number restricted - review the message and audience first.',
};

/** Plain words for Meta's common delivery errors (anything else shows Meta's own message). */
const FAILURE_LABEL: Record<string, string> = {
  '131049':
    "Meta held back this marketing message - the customer has had many recently. It doesn't count against you.",
  '131026': "The number isn't on WhatsApp or can't receive messages.",
  '131048': 'Spam warning - customers flagged messages from this number.',
  '131050': 'The customer turned off marketing messages from businesses.',
  META_PAIR_RATE_LIMIT: 'Too many messages to this customer in a short time.',
  META_RATE_LIMIT: 'Meta asked us to slow down.',
  META_SPAM_RATE_LIMIT: 'Spam warning - customers flagged messages from this number.',
  CONTACT_NOT_FOUND: 'The contact was deleted.',
};

const pct = (part: number, whole: number) =>
  whole > 0 ? ` (${Math.round((part / whole) * 100)}%)` : '';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

/**
 * How a started campaign is going: paused / waiting (with resume / pause),
 * the sending number's daily pace and how long the rest will take, and
 * Meta's delivery results with the reasons recipients failed.
 */
export function CampaignSendingCard({
  campaign: c,
  canManage,
}: {
  campaign: CampaignDetail;
  canManage: boolean;
}) {
  const pause = usePauseCampaign(c.id);
  const resume = useResumeCampaign(c.id);
  const toast = useToast();
  const active = c.status === 'QUEUED' || c.status === 'SENDING';
  const holding = !c.pausedAt && c.holdUntil && new Date(c.holdUntil).getTime() > Date.now();
  const pending = c.recipientCounts.pending;
  // Older API responses (mid-deploy) have no pace / receipts yet.
  const sending = c.sending ?? null;
  const cap = sending?.dailyCap ?? null;
  const failureReasons = c.failureReasons ?? [];
  const delivered = c.deliveredCount ?? 0;
  const read = c.readCount ?? 0;
  const days = cap && pending > cap ? Math.ceil(pending / cap) : null;
  const sent = c.sentCount;

  const run = (m: typeof pause, done: string) =>
    m.mutate(undefined, {
      onSuccess: () => toast.success(done),
      onError: (err) => toast.error(err.message),
    });

  return (
    <Card title="Sending">
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        {active && c.pausedAt && c.pauseReason ? (
          <div
            role="status"
            style={{
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3)',
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            <strong>{PAUSED[c.pauseReason]}</strong>
            {canManage ? (
              <div>
                <Button
                  disabled={resume.isPending}
                  onClick={() => run(resume, 'Campaign resumed.')}
                >
                  {resume.isPending ? 'Resuming…' : 'Resume sending'}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {active && holding ? (
          <p role="status" style={{ margin: 0 }}>
            {c.holdReason === 'DAILY_LIMIT'
              ? "Today's sending limit for this number is used up."
              : 'Meta asked us to slow down for a moment.'}{' '}
            It continues by itself at {new Date(c.holdUntil!).toLocaleString()}.
          </p>
        ) : null}

        {sending ? (
          <>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <span className="ne-badge">
                Messaging limit: {limitLabel(sending.messagingLimitTier)}
              </span>
              <QualityBadge rating={sending.qualityRating} />
            </div>
            <p style={{ ...muted, margin: 0 }}>
              {cap === null
                ? 'No daily cap - this number has no Meta messaging limit.'
                : `Campaigns on this number send up to ${cap.toLocaleString()} messages a day (the rest of ` +
                  `Meta's limit is kept for reminders and order updates); ${sending.sentLast24h.toLocaleString()} ` +
                  'sent in the last 24 hours.'}
              {(active || c.status === 'DRAFT') && days
                ? ` ${c.status === 'DRAFT' ? 'Once started, the' : 'The'} ${pending.toLocaleString()} ` +
                  `still waiting will go out over about ${days} days - customers who wrote to you ` +
                  'most recently first.'
                : ''}
            </p>
          </>
        ) : null}

        {sent + c.failedCount > 0 ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <span className="ne-badge">{sent} sent</span>
            <span className="ne-badge">
              {delivered} delivered{pct(delivered, sent)}
            </span>
            <span className="ne-badge">
              {read} read{pct(read, sent)}
            </span>
            <span className="ne-badge">{c.failedCount} failed</span>
          </div>
        ) : null}

        {failureReasons.length > 0 ? (
          <div>
            <strong style={{ fontSize: 'var(--font-size-sm)' }}>Why messages failed</strong>
            <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-5)' }}>
              {failureReasons.map((f) => (
                <li key={f.code} style={{ fontSize: 'var(--font-size-sm)' }}>
                  {FAILURE_LABEL[f.code] ?? (f.message || f.code)} - {f.count}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {canManage && active && !c.pausedAt ? (
          <div>
            <Button
              variant="secondary"
              disabled={pause.isPending}
              onClick={() => run(pause, 'Campaign paused.')}
            >
              Pause sending
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
